import type { FunctionComponent } from 'react';
import Highlighter from 'react-highlight-words';

import {
  Price,
  SkeletonLoader,
  type SkeletonLoaderProps,
  Text,
} from '@kanda-libs/ks-design-library';
import type { FinanceRate } from '@kanda-libs/ks-frontend-services';

import useLoanTableBreakDownProps from 'components/loan/LoanTable/LoanTableBreakDown/useLoanTableBreakDownProps';

export interface LoanTableBreakDownProps {
  loan?: number;
  deposit?: number;
  rate?: FinanceRate;
  xid?: string;
  isLoading?: boolean;
  skeletonClassName?: string;
  skeletonProps?: SkeletonLoaderProps;
}

const LoanTableBreakDown: FunctionComponent<LoanTableBreakDownProps> =
  function ({
    loan = 0,
    deposit = 0,
    xid,
    rate,
    isLoading = false,
    skeletonClassName = '',
    skeletonProps = {},
  }) {
    const { classNames, searchWords } = useLoanTableBreakDownProps();

    return (
      <SkeletonLoader
        isLoading={isLoading}
        className={skeletonClassName}
        {...skeletonProps}
        afterLoading={
          <div className="flex flex-col max-w-full">
            <div className="flex flex-row mb-0.5">
              <Text
                text={rate?.name?.replace(/_/g, ' ')}
                className="text-12-18-em"
              />
            </div>
            <div className="flex flex-row mb-0.5">
              <Text text="Loan: " className="text-12-18-em min-w-16" />
              <Price amount={loan} color="green-500" {...classNames} />
            </div>
            <div className="flex flex-row">
              <Text text="Deposit: " className="text-12-18-em min-w-16" />
              <Price amount={deposit} color="blue-200" {...classNames} />
            </div>
            {xid ? (
              <div className="flex flex-row">
                <Text text="XID:" className="text-12-18-em min-w-16" />
                <Highlighter
                  searchWords={searchWords}
                  textToHighlight={xid || ''}
                  className="text-12-18-em min-w-16"
                  highlightClassName="text-turquoise-300 bg-transparent"
                  autoEscape={false}
                />
              </div>
            ) : null}
          </div>
        }
      />
    );
  };

export default LoanTableBreakDown;
